import { ReactNode } from 'react';
import { useSearch } from '@tanstack/react-router';
import * as z from 'zod';
import { DateTime } from 'luxon';

import { useAuth } from './Auth.hooks';
import styles from './Auth.module.css';

import config from '@/config';
import { TemplatePadi } from '@/config.type';
import { NAVBAR_ICON_URL, USER_ROLE } from '@/constants';

import { Modal } from '@/components/modal/Modal';
import {
	ACCESS_TOKEN_EXPIRATION_KEY,
	AUTH_KEY,
} from '@/views/home/auth/Authentication.hooks';

export const clearToken = (code?: string) => {
	if (localStorage.getItem(USER_ROLE)) {
		localStorage.removeItem(USER_ROLE);
	}
	if (localStorage.getItem(AUTH_KEY)) {
		localStorage.removeItem(AUTH_KEY);
	}
	if (localStorage.getItem(ACCESS_TOKEN_EXPIRATION_KEY)) {
		localStorage.removeItem(ACCESS_TOKEN_EXPIRATION_KEY);
	}

	location.href = code ? `/callback?e=${code}` : '/callback';
};

export const Auth = ({ children }: AuthProps) => {
	const search = useSearch({ strict: false });
	const code = search.e;
	const [show] = useAuth();
	const isAuthenticated = localStorage.getItem(AUTH_KEY) === 'true';
	const userRole = localStorage.getItem(USER_ROLE);
	if (config?.app?.auth?.enabled && config.app?.auth.type === 'date-token') {
		if (!isAuthenticated) {
			clearToken(code);
			return null;
		}

		if (userRole === 'admin') {
			return <>{children}</>;
		}

		const expirationDate = localStorage.getItem(
			ACCESS_TOKEN_EXPIRATION_KEY,
		);
		if (!expirationDate) {
			clearToken(code);
			return null;
		}
		const accessExpirationDate = DateTime.fromISO(`${expirationDate}`);
		const now = DateTime.now();

		// If now is larger than refresh expiration date clear the tokens
		if (now >= accessExpirationDate) {
			alert('Token already expired');
			clearToken(code);
			return null;
		}
		return <>{children}</>;
	}
	const template = config.app.template as TemplatePadi;

	const redirectSections = [
		`?client_id=${template.config.clientId}&`,
		`redirect_uri=${location.origin}/callback&`,
		`extraData=${location.pathname}`,
	];
	const redirectSignUpUrl = [
		template.config.auth?.config.signUpUrl,
		...redirectSections,
	].join('');
	const redirectSignIn = [
		template.config.auth?.config.signInUrl,
		...redirectSections,
	].join('');
	if (!template?.config?.auth?.enabled) {
		return <>{children}</>;
	}
	return (
		<>
			{children}
			{show ? (
				<Modal
					hasCloseBtn={true}
					classNames={[styles['modal']]}
					isOpen={show}
					onClose={() => {
						location.href = redirectSignUpUrl;
					}}
				>
					<div>
						<div>
							<img src={NAVBAR_ICON_URL} alt="Logo" />
							<h3>Want to read more?</h3>
							<p>Sign up for access to Scuba Diving Magazine!</p>
							<button
								style={{
									cursor: 'pointer',
								}}
								onClick={() => {
									location.href = redirectSignUpUrl;
								}}
							>
								Sign Up Now
							</button>
							<footer>
								Already have an Account?{' '}
								<a target="_self" href={redirectSignIn}>
									Sign In
								</a>
							</footer>
						</div>
					</div>
				</Modal>
			) : null}
		</>
	);
};

interface AuthProps {
	children: ReactNode;
}
